import { LabelIcons } from "PFComponents/label";
import { Id } from "PFTypes";
import { FetchOptionsResponse, GroupOption, Option, OptionOriginal } from "PFTypes/options";
import { ReactNode, RefObject } from "react";

type FlatOptions<T> = {
  options: Option<T>[];
  onCreateOption?: (option: Option<T>) => void;
};

type GroupOptions<T> = {
  options: GroupOption<T>[];
  onCreateOption?: never;
};

type StaticOptions<T> = {
  fetchOptions?: never;
  cacheTime?: never;
} & (FlatOptions<T> | GroupOptions<T>);

type FetchOptions<T> = {
  options?: never;
  fetchOptions: (payload: { ids?: Id[]; searchTerm?: string }) => Promise<FetchOptionsResponse<T>>;
  onCreateOption?: (option: Option<T>) => Promise<Option<T>>;
  cacheTime?: number;
};

type SingleValue<T> = {
  multiple?: false;
  value?: Id;
  limit?: never;
  renderDisplayValue?: (value: Option<T> | undefined) => ReactNode;
};

type MultipleValue<T> = {
  multiple: true;
  value: Id[];
  limit?: number;
  renderDisplayValue?: (value: Option<T>[]) => ReactNode;
};

export type SelectCommonProps<T> = {
  id?: string;
  searchId?: Id;
  inline?: boolean;
  label?: string;
  labelIcons?: LabelIcons;
  placeholder?: string;
  required?: boolean;
  onRestore?: VoidFunction;
  onClear?: VoidFunction;
  searchableVariant?: SearchableVariant;
  focusSearchInputOnOpen?: boolean;
  disabled?: boolean;
  locked?: boolean;
  displayValueBelow?: boolean;
  dropdownId?: string;
  onChange: (options: Option<T> | Option<T>[], other?: any) => void;
  onDropdownClose?: VoidFunction;
  createOptionBlocklist?: string[];
  restoreLabel?: string;
  tip?: string;
  warning?: string;
  lockedTip?: string;
  error?: string;
  isFetchingSelectedOptions?: boolean;
  parseOptions?: (options: Option<T>[] | GroupOption<T>[]) => Option<T>[] | GroupOption<T>[];
  dropdownWidth?: number | "fit-content";
  dropdownMaxHeight?: number;
  valueBoxWidth?: number | string;
  qaId?: string;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
  tabIndex?: number;
  ref?: React.RefObject<HTMLDivElement | null>;
  inputRef?: React.RefObject<HTMLDivElement | null>;
  clipOptionsOverflow?: boolean;
  noOptionsText?: string | ((searchTerm: string) => string);
  dropdownHeaderElement?: (options: Option<T>[] | GroupOption<T>[]) => ReactNode;
  noPortal?: boolean;
  createButton?: {
    label?: string;
    tooltipContent?: ReactNode;
  };
  variant?: "default" | "table";
  portalRef?: RefObject<HTMLElement | null>;
};

export enum SearchableVariant {
  Always = "always",
  Never = "never",
  Dynamic = "dynamic"
}

export type OptionsProps<T> = StaticOptions<T> | FetchOptions<T>;

export type ValueProps<T> = SingleValue<T> | MultipleValue<T>;

export type SelectProps<T = OptionOriginal> = SelectCommonProps<T> & OptionsProps<T> & ValueProps<T>;
